import type { CircleLayerSpecification, Map as MapboxMap } from "mapbox-gl";
import { makeAutoObservable } from "mobx";
import { MapBoxSource } from "./MapBoxSource";
import { MapBoxLayer, type LayerEvt } from "./MapBoxLayer";
import { MarkerLayerConfig } from "./MapStore";

export type LngLatTuple = [number, number];

export class MarkersStore {
	private source: MapBoxSource;
	private shadowLayer: MapBoxLayer<CircleLayerSpecification>;
	private layer: MapBoxLayer<CircleLayerSpecification>;

	// @ts-ignore
	private layerConfig?: MarkerLayerConfig;

	selectedId: string | null = null;
	hoveredId: string | null = null;

	constructor(source: MapBoxSource, layerId = "markers-layer", layerConfig?: MarkerLayerConfig) {
		this.source = source;
		this.layerConfig = layerConfig;
		const defaultPaint = {
			"circle-radius": 5,
			"circle-color": "#ef4444",
			"circle-stroke-color": "#ffffff",
			"circle-stroke-width": 1,
		};

		const mergedPaint = {
			...defaultPaint,
			...(layerConfig?.paint ?? {}),
		};

		// Drop shadow sits below the markers, mirroring their radius so it scales
		// with whatever paint the consumer provided. Matches the original page map.
		const shadowLayerSpec: CircleLayerSpecification = {
			id: `${layerId}-shadow`,
			type: "circle",
			source: this.source.id,
			filter: ["!", ["has", "point_count"]],
			paint: {
				"circle-color": "#000000",
				"circle-opacity": 0.15,
				"circle-blur": 0.5,
				"circle-radius": mergedPaint["circle-radius"],
				"circle-translate": [0, 1.5],
			},
		};

		const layerSpec: CircleLayerSpecification = {
			id: layerId,
			type: "circle",
			source: this.source.id,
			filter: ["!", ["has", "point_count"]],
			paint: mergedPaint,
			...(layerConfig?.layout ? { layout: layerConfig.layout } : {}),
			...(layerConfig?.minzoom !== undefined ? { minzoom: layerConfig.minzoom } : {}),
			...(layerConfig?.maxzoom !== undefined ? { maxzoom: layerConfig.maxzoom } : {}),
		};

		this.shadowLayer = new MapBoxLayer({ id: shadowLayerSpec.id, spec: shadowLayerSpec });
		this.layer = new MapBoxLayer({ id: layerId, spec: layerSpec });

		makeAutoObservable(this);
	}

	attach(map: MapboxMap) {
		this.source.attach(map);
		this.shadowLayer.attach(map);
		this.layer.attach(map);

		// events
		this.layer.onClick(this.handleClick, { idProp: "id" });
		this.layer.onMouseEnter(this.handleEnter, { idProp: "id" });
		this.layer.onMouseLeave(() => {
			this.hoveredId = null;
		});
	}

	detach() {
		this.shadowLayer.detach();
		this.layer.detach();
	}

	// Public marker-event hooks. Pipe extra subscribers (e.g. <MapPopups>) into the
	// same mapbox layer events the internal click/hover handlers use. Returns `this`
	// for chaining, matching MapBoxLayer's API.
	onMarkerClick = (handler: (event: LayerEvt) => void): this => {
		this.layer.onClick(handler, { idProp: "id" });
		return this;
	};

	onMarkerEnter = (handler: (event: LayerEvt) => void): this => {
		this.layer.onMouseEnter(handler, { idProp: "id" });
		return this;
	};

	onMarkerMouseMove = (handler: (event: LayerEvt) => void): this => {
		this.layer.onMouseMove(handler, { idProp: "id" });
		return this;
	};

	onMarkerMouseLeave = (handler: (event: { event: unknown; map: MapboxMap }) => void): this => {
		this.layer.onMouseLeave(handler);
		return this;
	};

	/**
	 * Apply a new layer config to the live layer (paint + layout only).
	 * Safe to call before/after attach: setPaint/setLayout no-op if the layer isn't on the map yet.
	 * Note: the underlying layer spec is fixed at construction; this updates *runtime* properties.
	 */
	applyLayerConfig(layerConfig: MarkerLayerConfig) {
		this.layerConfig = layerConfig;
		if (layerConfig.paint) {
			for (const [name, value] of Object.entries(layerConfig.paint)) {
				this.layer.setPaint(name as any, value as any);
				// Keep the shadow's radius in sync with the markers so the shadow
				// stays underneath as paint changes (e.g. perspective swap).
				if (name === "circle-radius") {
					this.shadowLayer.setPaint("circle-radius" as any, value as any);
				}
			}
		}
		if (layerConfig.layout) {
			for (const [name, value] of Object.entries(layerConfig.layout)) {
				this.layer.setLayout(name as any, value as any);
			}
		}
	}

	private handleClick = (evt: LayerEvt) => {
		this.selectedId = evt.id != null ? String(evt.id) : null;
	};

	private handleEnter = (evt: LayerEvt) => {
		this.hoveredId = evt.id ?? null;
	};

	clearSelection = () => {
		this.selectedId = null;
	};

	setMarkers<TData>(
		data: TData[],
		getCoordinates: (item: TData) => LngLatTuple,
		getId: (item: TData) => string,
		getProperties?: (item: TData) => Record<string, unknown>,
	) {
		const fc: GeoJSON.FeatureCollection = {
			type: "FeatureCollection",
			features: data.map(item => {
				const [lng, lat] = getCoordinates(item);
				return {
					type: "Feature" as const,
					properties: {
						id: getId(item),
						...(getProperties ? getProperties(item) : {}), // 👈 MERGE CUSTOM PROPS
					},
					geometry: {
						type: "Point" as const,
						coordinates: [lng, lat],
					},
				};
			}),
		};

		this.source.setGeoJSONData(fc);
	}
}
